import { TextInput, Box } from "@mantine/core";
import { useTaxOrganizerStore } from "../../../../../utils/stores/taxOrganizerStore";
import "./DifferentDaycares.scss";
import type { Dependent, SchemaShape } from "../../../../../types";
import { validateField } from "../../../../../utils/configs/formSchema";

interface DifferentDaycareProps {
  groupIndex: number;
  parentObjectName: string;
  labelOne: string;
  labelTwo: string;
  labelThree: string;
  disabled?: boolean; // Add the optional disabled prop
  errorMessages?: Record<string, string>;
}

const DifferentDaycares = ({
  groupIndex,
  parentObjectName,
  labelOne,
  labelTwo,
  labelThree,
  disabled = false, // Default disabled to false
  errorMessages,
}: DifferentDaycareProps) => {
  const { responses, updateResponses, setValidationErrors } =
    useTaxOrganizerStore();

  const getDaycareErrorMessage = (index: number, field: string) => {
    const itemErrorKey = `${parentObjectName}[${groupIndex}].${storedObjectName}[${index}].${field}`;
    const errorMessage = errorMessages?.[itemErrorKey] ?? "";

    // Extract the last field from the error message
    const lastField = itemErrorKey.split(".").pop() ?? "";

    return errorMessage ?? `${lastField} is required`;
  };

  const storedObjectName = "differentDaycares";

  //dependent specific
  const childrenWithDaycare: Dependent[] =
    responses?.dependentsInfo.filter(
      (child: any) => child.dependentsDaycareChoice === "Yes"
    ) || [];

  const childnameslist = childrenWithDaycare.map((child, index) => {
    const fullName = `${child?.firstName ?? ""} ${child?.middleName ?? ""} ${
      child?.lastName ?? ""
    }`;
    return fullName.trim() === "" ? `Dependent ${index + 1}` : fullName.trim();
  });

  const itemsCount = childrenWithDaycare.length;

  const handleUpdate = async (index: number, field: string, value: string) => {
    const updatedParentObject = [...(responses[parentObjectName] || [])];

    updatedParentObject[groupIndex] = {
      ...updatedParentObject[groupIndex],
      [storedObjectName]: [
        ...(updatedParentObject[groupIndex]?.[storedObjectName] || []),
      ],
    };

    updatedParentObject[groupIndex][storedObjectName][index] = {
      ...updatedParentObject[groupIndex][storedObjectName][index],
      [field]: value,
    };

    updateResponses({ [parentObjectName]: updatedParentObject });

    // Revalidate the field
    const errorMessage = await validateField(
      parentObjectName as keyof SchemaShape,
      groupIndex,
      `${storedObjectName}[${index}].${field}`,
      value,
      updatedParentObject[groupIndex]
    );

    setValidationErrors((prev) => ({
      ...prev,
      [`${parentObjectName}[${groupIndex}].${storedObjectName}[${index}].${field}`]:
        errorMessage || "",
    }));
  };

  return (
    <div
      className="different-daycares-container"
      style={{ flexDirection: "row" }}
    >
      {[...Array(itemsCount)].map((item, index) => (
        <Box key={`${item}-${index}`} className="different-daycare-row">
          <h3>{childnameslist[index]}</h3>
          <TextInput
            name={`daycareName-${index}`}
            label={labelOne}
            value={
              responses[parentObjectName]?.[groupIndex]?.[storedObjectName]?.[
                index
              ]?.[labelOne] ?? ""
            }
            required={true}
            onChange={(event) =>
              handleUpdate(index, labelOne, event.currentTarget.value)
            }
            style={{ flexGrow: 1, marginRight: "10px" }}
            disabled={disabled}
            error={!disabled && getDaycareErrorMessage(index, labelOne)}
          />
          <TextInput
            name={`daycareEIN-${index}`}
            label={labelTwo}
            value={
              responses[parentObjectName]?.[groupIndex]?.[storedObjectName]?.[
                index
              ]?.[labelTwo] ?? ""
            }
            required={true}
            onChange={(event) =>
              handleUpdate(index, labelTwo, event.currentTarget.value)
            }
            style={{ flexGrow: 1, marginRight: "10px" }}
            disabled={disabled}
            error={!disabled && getDaycareErrorMessage(index, labelTwo)}
          />
          <TextInput
            name={`daycareTuitionAmount-${index}`}
            label={labelThree}
            value={
              responses[parentObjectName]?.[groupIndex]?.[storedObjectName]?.[
                index
              ]?.[labelThree] ?? ""
            }
            leftSection={"$"}
            required={true}
            onChange={(event) =>
              handleUpdate(index, labelThree, event.currentTarget.value)
            }
            style={{ flexGrow: 1, marginRight: "10px" }}
            disabled={disabled}
            error={!disabled && getDaycareErrorMessage(index, labelThree)}
          />
        </Box>
      ))}
    </div>
  );
};

export default DifferentDaycares;
